import { formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CompositeFilterDescriptor, State, toODataString } from '@progress/kendo-data-query';
import { NotificationService } from '@progress/kendo-angular-notification';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ReportJobService, ReportJobConfig } from 'src/app/core/services/reporting/report-job.service';

/** Options for queuing an EDR CSV export. */
export interface EdrCsvExportOptions {
  /** Report type key, e.g. 'edr-open-threats-report'. */
  reportKey: string;
  organizationId: number | null;
  /** [start, end] ISO strings from the EDR tab period selector. */
  dateRange: string[] | null;
  /**
   * Current grid column filter. Serialized to an OData $filter and sent as a
   * report param so the backend can narrow the export to match the table view.
   * The backend support for this lands in a follow-up; until then it is ignored.
   */
  filter?: CompositeFilterDescriptor;
}

/**
 * Shared CSV export for the EDR Analytics detail views.
 *
 * Queues a server-side report job (same backend query the grid uses, so the CSV
 * matches the table) and reports outcome via notifications. Callers manage their
 * own loading flag around the returned observable.
 */
@Injectable({ providedIn: 'root' })
export class EdrCsvExportService {
  private http = inject(HttpClient);
  private reportJobService = inject(ReportJobService);
  private notificationService = inject(NotificationService);
  private apiUrl = environment.apiUrl;

  /** reportKey -> report_template_id, resolved once per key. */
  private templateIdCache = new Map<string, number>();

  /**
   * Queue a CSV export for the given EDR report. Completes once the job is
   * queued; errors if org/date/template are missing or the request fails.
   */
  public export(options: EdrCsvExportOptions): Observable<void> {
    const orgId = options.organizationId;
    const startDate = options.dateRange?.[0];
    const endDate = options.dateRange?.[1];

    if (!orgId || !startDate || !endDate) {
      this.notify('Unable to export: missing organization or date range.', 'warning');
      return throwError(() => new Error('missing-org-or-date'));
    }

    return this.resolveTemplateId(options.reportKey).pipe(
      switchMap((templateId) => {
        if (templateId == null) {
          this.notify('Unable to export: report is not available.', 'warning');
          return throwError(() => new Error('missing-template'));
        }

        const filterExpression = this.buildFilterExpression(options.filter);
        const config: ReportJobConfig = {
          organizations: [orgId],
          primary_organization_id: orgId,
          report_template_id: templateId,
          start_date: formatDate(startDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
          end_date: formatDate(endDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
          delivery_method: 'download',
          report_params: filterExpression ? { odata_filter: filterExpression } : undefined,
        };

        return this.reportJobService.createReportJob(config);
      }),
      tap(() =>
        this.notify(
          'Export queued. You will find the CSV in the Reports history once generated.',
          'success',
        ),
      ),
      map(() => void 0),
      catchError((error) => {
        // The two guards above already notified; only surface unexpected failures.
        if (error?.message !== 'missing-org-or-date' && error?.message !== 'missing-template') {
          this.notify('Failed to queue export. Please try again.', 'error');
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Serialize a Kendo grid filter to an OData $filter expression, or null when
   * there is no active filter. Mirrors the $filter the grid already sends, so
   * the backend can parse it with the same logic as the detail endpoint.
   */
  public buildFilterExpression(filter?: CompositeFilterDescriptor): string | null {
    if (!filter || !filter.filters?.length) {
      return null;
    }
    const query = toODataString({ filter } as State);
    const match = query.match(/\$filter=(.*)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private resolveTemplateId(reportKey: string): Observable<number | null> {
    const cached = this.templateIdCache.get(reportKey);
    if (cached != null) {
      return of(cached);
    }

    const url = `${this.apiUrl}/v2/reporting/api/report-types/?$filter=key eq '${reportKey}'`;
    return this.http.get<{ value: { report_template?: { id?: number } }[] }>(url).pipe(
      map((response) => response.value?.[0]?.report_template?.id ?? null),
      tap((id) => {
        if (id != null) {
          this.templateIdCache.set(reportKey, id);
        }
      }),
    );
  }

  private notify(content: string, style: 'success' | 'warning' | 'error'): void {
    this.notificationService.show({
      content,
      type: { style, icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 400 },
      closable: true,
      hideAfter: 5000,
    });
  }
}
